/**
 * formatMoney — THE single money formatter of the frontend.
 *
 * One generic tool, never one code path per currency: the backend sends numbers
 * plus a `currency` field (see docs/implementations/group/GROUP_REPORTING_CONTRACT.md),
 * the consumer formats with this function and nothing else.
 *
 *   formatMoney(1234.5)                     → "1 234,50 €"
 *   formatMoney(1234.5, 'PLN')              → "1234,50 zł"
 *   formatMoney(12345, 'EUR', { compact: true }) → "12,3 k€"
 *   formatMoney(null)                       → "0,00 €"
 *   formatMoney(1234.5, 'XXX')              → "1 234,50 XXX"  (unknown ISO code)
 *
 * @param {number|string|null|undefined} amount
 * @param {string} [currency='EUR'] ISO 4217 code
 * @param {Object} [options]
 * @param {boolean} [options.compact=false] "12,3 k€" style for tiles (k / M)
 * @param {number}  [options.decimals=2]    fraction digits (ignored in compact mode)
 * @param {string}  [options.locale]        override the locale inferred from the currency
 * @returns {string}
 */

// Locale inferred from the currency — the only per-currency knowledge in the frontend.
const LOCALE_BY_CURRENCY = {
  EUR: 'fr-FR',
  PLN: 'pl-PL',
  SEK: 'sv-SE',
};
const DEFAULT_LOCALE = 'fr-FR';
const DEFAULT_CURRENCY = 'EUR';

const formatterCache = new Map();

function toNumber(amount) {
  const n = typeof amount === 'string' ? parseFloat(amount.replace(',', '.')) : Number(amount);
  return Number.isFinite(n) ? n : 0;
}

function normalizeCurrency(currency) {
  const code = String(currency || DEFAULT_CURRENCY).trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : DEFAULT_CURRENCY;
}

export function localeForCurrency(currency) {
  return LOCALE_BY_CURRENCY[normalizeCurrency(currency)] || DEFAULT_LOCALE;
}

function getFormatter(locale, currency, decimals) {
  const key = `${locale}|${currency}|${decimals}`;
  let fmt = formatterCache.get(key);
  if (fmt) return fmt;
  try {
    fmt = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    // Intl renders the generic sign "¤" for codes it has no symbol for → treat as unknown
    if (fmt.formatToParts(0).some((p) => p.type === 'currency' && p.value === '¤')) throw new Error('unknown currency');
  } catch {
    // Unknown ISO code for this engine → plain number + code suffix
    const plain = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    fmt = { format: (v) => `${plain.format(v)} ${currency}` };
  }
  formatterCache.set(key, fmt);
  return fmt;
}

/** Currency symbol alone ("€", "zł", "kr") — falls back to the ISO code. */
export function currencySymbol(currency, locale) {
  const code = normalizeCurrency(currency);
  const loc = locale || localeForCurrency(code);
  try {
    const parts = new Intl.NumberFormat(loc, { style: 'currency', currency: code }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency')?.value;
    return sym && sym !== '¤' ? sym : code;
  } catch {
    return code;
  }
}

export function formatMoney(amount, currency = DEFAULT_CURRENCY, { compact = false, decimals = 2, locale } = {}) {
  const value = toNumber(amount);
  const code = normalizeCurrency(currency);
  const loc = locale || localeForCurrency(code);

  if (compact) {
    const abs = Math.abs(value);
    const symbol = currencySymbol(code, loc);
    const plain = (v, d) => new Intl.NumberFormat(loc, { minimumFractionDigits: 0, maximumFractionDigits: d }).format(v);
    if (abs >= 1_000_000) return `${plain(value / 1_000_000, 1)} M${symbol}`;
    if (abs >= 1_000) return `${plain(value / 1_000, 1)} k${symbol}`;
    return getFormatter(loc, code, 0).format(value);
  }

  const safeDecimals = Number.isInteger(decimals) && decimals >= 0 ? Math.min(decimals, 6) : 2;
  return getFormatter(loc, code, safeDecimals).format(value);
}

export default formatMoney;
